import { useCallback, useEffect, useLayoutEffect, useState, useSyncExternalStore } from "react";

const STORAGE_KEY = "because-fm-reading-progress-v1";
const OWNER_KEY = "because-fm-reading-owner-v1";
const MAX_ENTRIES = 300;
/** Пачку копим несколько секунд: длинный «Сериал» иначе превратится в поток запросов. */
const PUSH_DELAY = 4000;
const MAX_PUSH = 100;

type ProgressEntry = { percent: number; updatedAt: number };

function readStore(): Record<string, ProgressEntry> {
  try {
    const parsed = JSON.parse(localStorage.getItem(STORAGE_KEY) || "null") as Record<string, ProgressEntry> | null;
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch { return {}; }
}

function writeStore(store: Record<string, ProgressEntry>) {
  try {
    let entries = Object.entries(store);
    if (entries.length > MAX_ENTRIES) entries = entries.sort((a, b) => b[1].updatedAt - a[1].updatedAt).slice(0, MAX_ENTRIES);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(Object.fromEntries(entries)));
  } catch { /* приватный режим не должен ломать чтение */ }
}

// Полка «Продолжить чтение» читает это хранилище напрямую, а не из состояния
// React. Чтобы она увидела прогресс, приехавший с сервера, держим счётчик
// версий и подписку на него: без этого библиотека показывала бы старые проценты
// до следующей перерисовки по другой причине.
let version = 0;
const listeners = new Set<() => void>();
function bumpVersion() { version += 1; for (const listener of listeners) listener(); }
function subscribe(listener: () => void) { listeners.add(listener); return () => { listeners.delete(listener); }; }
function snapshot() { return version; }

export function useReadingProgressVersion() {
  return useSyncExternalStore(subscribe, snapshot, snapshot);
}

export function readingProgressPercent(key: string): number {
  const entry = readStore()[key];
  return entry ? Math.min(100, Math.max(0, entry.percent)) : 0;
}

// Отдаёт всё локальное хранилище прогресса — нужно, чтобы собирать полку
// «Продолжить чтение» и сортировать её по дате последнего чтения.
export function readingProgressEntries(): Array<{ key: string; percent: number; updatedAt: number }> {
  return Object.entries(readStore()).map(([key, entry]) => ({
    key,
    percent: Math.min(100, Math.max(0, entry.percent)),
    updatedAt: Number(entry.updatedAt) || 0,
  }));
}

// Синхронизация с сервером. Локальное хранилище остаётся источником для чтения:
// оно отвечает мгновенно, работает без сети и обслуживает анонимного читателя.
// Сервер добавляет к нему то, что прочитано на других устройствах.
let syncUserId: string | null = null;
let pending = new Set<string>();
let pushTimer = 0;

function mergeEntry(store: Record<string, ProgressEntry>, key: string, percent: number, updatedAt: number) {
  const previous = store[key];
  const furthest = Math.max(previous?.percent || 0, percent);
  store[key] = { percent: Math.min(100, Math.round(furthest * 10) / 10), updatedAt: Math.max(previous?.updatedAt || 0, updatedAt) };
}

async function pushProgress(keepalive = false) {
  if (!syncUserId || !pending.size) return;
  const store = readStore();
  const entries = [...pending].slice(0, MAX_PUSH)
    .map((key) => ({ key, percent: store[key]?.percent || 0 }))
    .filter((entry) => entry.percent > 0);
  pending = new Set();
  if (!entries.length) return;
  try {
    await fetch("/api/reading-progress", {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ entries }), keepalive,
    });
  } catch {
    // Место остановки уже сохранено локально, поэтому молчим и возвращаем
    // записи в очередь: дошлём следующей пачкой или при следующем открытии.
    for (const entry of entries) pending.add(entry.key);
  }
}

function schedulePush() {
  if (!syncUserId || pushTimer) return;
  pushTimer = window.setTimeout(() => { pushTimer = 0; void pushProgress(); }, PUSH_DELAY);
}

/**
 * Привязать прогресс к аккаунту: подтянуть серверный, слить с локальным по
 * дальней точке и отправить то, чего сервер ещё не знает.
 *
 * Прочитанное до входа не теряется — анонимный кэш вливается в аккаунт. А вот
 * чужой кэш вливать нельзя: если на этом устройстве до нас читал другой
 * человек, его проценты сначала вычищаются, иначе он оставит след в чужой
 * полке «Продолжить чтение».
 */
export async function attachReadingProgress(userId: string | null) {
  syncUserId = userId;
  if (!userId) return;
  let owner = "";
  try { owner = localStorage.getItem(OWNER_KEY) || ""; } catch { /* см. writeStore */ }
  if (owner && owner !== userId) {
    writeStore({});
    pending = new Set();
  }
  try { localStorage.setItem(OWNER_KEY, userId); } catch { /* см. writeStore */ }

  try {
    const response = await fetch("/api/reading-progress");
    if (!response.ok) return;
    const data = await response.json() as { entries?: Array<{ key: string; percent: number; updatedAt: string }> };
    const store = readStore();
    const remote = new Map<string, number>();
    for (const entry of data.entries || []) {
      if (!entry?.key) continue;
      remote.set(entry.key, entry.percent);
      mergeEntry(store, entry.key, Number(entry.percent) || 0, Date.parse(entry.updatedAt) || 0);
    }
    writeStore(store);
    bumpVersion();
    // Локальное, что ушло дальше серверного (читали с этого устройства без
    // входа или пока лежала сеть), догоняем в ту же сторону.
    for (const [key, entry] of Object.entries(store)) {
      if (entry.percent > (remote.get(key) || 0)) pending.add(key);
    }
    await pushProgress();
  } catch { /* без сети остаёмся на локальном кэше */ }
}

function saveProgress(key: string, percent: number) {
  const store = readStore();
  // Это не «текущий скролл», а самая дальняя прочитанная точка. Читатель
  // может вернуться к началу, чтобы перейти в библиотеку, но это не должно
  // превращать уже прочитанный выпуск обратно в непрочитанный.
  const previous = store[key]?.percent || 0;
  const furthest = Math.max(previous, percent);
  if (furthest < 0.5) return;
  store[key] = { percent: Math.min(100, Math.round(furthest * 10) / 10), updatedAt: Date.now() };
  writeStore(store);
  bumpVersion();
  pending.add(key);
  schedulePush();
}

/**
 * Запоминает самую дальнюю прочитанную точку и при повторном открытии возвращает к ней.
 * Дочитанные (< 0.5% или ≥ 99.5%) не восстанавливаются: пролог и финал удобнее начинать с начала.
 *
 * Возвращает процент, с которого продолжили, — ридер показывает по нему плашку.
 * Без неё возврат выглядит так, будто история открылась где-то в середине сама
 * по себе, и первым делом читатель ищет, куда делось начало.
 */
export function useReadingProgress(key: string) {
  const [resumedAt, setResumedAt] = useState<number | null>(null);
  // Restore against the committed reader layout before the first paint. Covers and
  // illustrations reserve their height, so a delayed scroll only causes a visible jump.
  useLayoutEffect(() => {
    const saved = readingProgressPercent(key);
    const resume = !window.location.hash && saved > 0.5 && saved < 99.5;
    const max = Math.max(0, document.documentElement.scrollHeight - window.innerHeight);
    if (!window.location.hash) window.scrollTo({ top: resume ? max * saved / 100 : 0, behavior: "instant" });
    setResumedAt(resume && max > 0 ? saved : null);
  }, [key]);

  useEffect(() => {
    // Прогресс пишем только по реальным scroll-событиям: занавес навигации скроллит
    // страницу к нулю уже после отписки, и это не должно затирать сохранённое место.
    let lastPercent = -1;
    let timer = 0;
    const measure = () => {
      const max = document.documentElement.scrollHeight - window.innerHeight;
      if (max > 0) lastPercent = window.scrollY / max * 100;
    };
    const flush = () => { if (lastPercent >= 0) saveProgress(key, lastPercent); };
    const onScroll = () => {
      measure();
      if (!timer) timer = window.setTimeout(() => { timer = 0; flush(); }, 900);
    };
    // Уход со страницы — последний шанс отправить пачку, поэтому не ждём
    // дебаунс и просим браузер дослать запрос уже после выгрузки вкладки.
    const onHide = () => { if (document.visibilityState === "hidden") { flush(); void pushProgress(true); } };
    const onLeave = () => { flush(); void pushProgress(true); };

    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("pagehide", onLeave);
    document.addEventListener("visibilitychange", onHide);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("pagehide", onLeave);
      document.removeEventListener("visibilitychange", onHide);
      if (timer) window.clearTimeout(timer);
      flush();
    };
  }, [key]);
  const forgetResume = useCallback(() => setResumedAt(null), []);
  return { resumedAt, forgetResume };
}
